import { PayoutBy } from 'features/common/hooks/useChart/types';
import { Highlights, SalesTicketType } from './types';

export const convertToKey = (date: Date | string): string =>
	typeof date === 'string' ? `${new Date(date).valueOf()}` : `${date.valueOf()}`;

// Strips the time component from a date, returning UTC midnight for that calendar day.
// Ensures same-day transactions from the API (which may carry varying timestamps) share a key.
const toUTCDay = (date: Date | string): Date => {
	const d = new Date(date as string);
	return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};

// Merges two arrays of SalesTicketType by name, summing sold and payout for duplicates.
const mergeSalesTicketTypes = (existing: SalesTicketType[], incoming: SalesTicketType[]): SalesTicketType[] => {
	const byName: Record<string, SalesTicketType> = {};
	for (const tt of existing) {
		byName[tt.name] = { ...tt };
	}
	for (const tt of incoming) {
		if (byName[tt.name]) {
			byName[tt.name].sold += tt.sold;
			byName[tt.name].payout += tt.payout;
		} else {
			byName[tt.name] = { ...tt };
		}
	}
	return Object.values(byName);
};

export const getDays = (data: Highlights<string>) => {
	const salesbyDay: PayoutBy = {};

	for (const [i, salesDate] of data.sales.entries()) {
		const { date, payout, ticket_types } = salesDate;
		const dayDate = toUTCDay(date);
		const key = convertToKey(dayDate);
		if (salesbyDay[key]) {
			salesbyDay[key].payout += payout;
			salesbyDay[key].ticket_types = mergeSalesTicketTypes(salesbyDay[key].ticket_types, ticket_types);
		} else {
			salesbyDay[key] = { date: dayDate, payout, ticket_types: [...ticket_types] };
		}

		// Fill in zero-sales days between consecutive sale records.
		// Uses normalized UTC days so gap keys are consistent with sale entry keys.
		const nextSale = data.sales[i + 1];
		if (nextSale) {
			const nextDayDate = toUTCDay(nextSale.date);
			const gapCursor = new Date(dayDate);
			while (nextDayDate.valueOf() - gapCursor.valueOf() > 24 * 60 * 60 * 1000) {
				gapCursor.setUTCDate(gapCursor.getUTCDate() + 1);
				const gapKey = convertToKey(gapCursor);
				if (!salesbyDay[gapKey]) {
					salesbyDay[gapKey] = { date: new Date(gapCursor), payout: 0, ticket_types: [] };
				}
			}
		}
	}

	return { ...data, sales: Object.values(salesbyDay || {}) };
};
